import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useMasters } from "@/lib/masters";
import { PageHeader, Card, Input, Btn, Select } from "@/components/kit";
import { useAuth } from "@/lib/auth";
import {
  ArrowClockwise,
  Briefcase,
  Fire,
  Stack,
  UsersThree,
} from "@phosphor-icons/react";

const WORKSHOPS = ["AMSKY", "CRON", "KODAK"];

function todayValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function formatDay(value, options = { day: "2-digit", month: "short" }) {
  if (!value) return "-";
  return new Date(value + "T00:00:00").toLocaleDateString("en-IN", options);
}

function MetricCard({ label, value, hint, icon: Icon, accent, testid }) {
  return (
    <Card
      testid={testid}
      className="p-4 relative overflow-hidden transition-transform duration-200 hover:-translate-y-0.5"
    >
      <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent }} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
          <div className="font-heading font-black text-3xl mt-1 tracking-tight text-zinc-950">
            {value ?? 0}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>
        </div>
        <div className="rounded-full p-2.5" style={{ backgroundColor: accent + "18", color: accent }}>
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </Card>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="px-4 py-3 border-b border-zinc-200">
      <div className="font-heading font-bold text-sm uppercase tracking-wider text-zinc-950">{title}</div>
      {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

function machineAccent(name, index) {
  const upper = String(name || "").toUpperCase();
  if (upper.includes("AMSKY")) return "#08a9d6";
  if (upper.includes("KODAK")) return "#df1683";
  if (upper.includes("CRON")) return "#f2c500";
  if (upper.includes("PARIBESHAK")) return "#18181b";
  return ["#08a9d6", "#df1683", "#f2c500", "#18181b"][index % 4];
}

export default function Dashboard() {
  const { user } = useAuth();
  const masters = useMasters();
  const canManageAllWorkshops = ["super_admin", "admin", "manager"].includes(user?.role);
  const assignedWorkshop = (user?.assigned_workshop || "AMSKY").toUpperCase();
  const [selectedDate, setSelectedDate] = useState(todayValue);
  const [workshopFilter, setWorkshopFilter] = useState(canManageAllWorkshops ? "" : assignedWorkshop);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get("/reports/dashboard", {
        params: { selected_date: selectedDate, ...(workshopFilter ? { workshop: workshopFilter } : {}) },
      });
      setData(response.data);
      setError("");
      setLastUpdated(new Date());
    } catch (err) {
      console.error("dashboard load failed:", err?.message || err);
      setError(err?.response?.data?.detail || "Could not load the dashboard.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [selectedDate, workshopFilter]);

  useEffect(() => {
    load(false);
    const timer = window.setInterval(() => load(true), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const machines = useMemo(
    () => (masters.machines || []).filter(machine =>
      !workshopFilter || machine.name?.toUpperCase() === workshopFilter || machine.code?.toUpperCase() === workshopFilter
    ),
    [masters.machines, workshopFilter],
  );
  const trend = useMemo(() => data?.seven_day_trend || [], [data?.seven_day_trend]);
  const maxJobs = useMemo(() => Math.max(1, ...trend.map((row) => row.jobs || 0)), [trend]);
  const maxPlates = useMemo(() => Math.max(1, ...trend.map((row) => row.plates || 0)), [trend]);

  const headerActions = (
    <div className="flex flex-wrap items-end gap-2">
      <label className="block">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
          Workshop
        </span>
        <Select value={workshopFilter} onChange={event => setWorkshopFilter(event.target.value)} className="bg-white min-w-[130px]" data-testid="dashboard-workshop-filter">
          <option value="">All</option>
          {WORKSHOPS.map(workshop => <option key={workshop} value={workshop}>{workshop}</option>)}
        </Select>
      </label>
      <label className="block">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
          View date
        </span>
        <Input
          type="date"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
          data-testid="dashboard-date-filter"
          className="bg-white"
        />
      </label>
      <Btn
        variant="outline"
        onClick={() => load(false)}
        disabled={loading}
        data-testid="dashboard-refresh"
        title="Refresh dashboard"
      >
        <ArrowClockwise size={15} className={loading ? "animate-spin" : ""} />
        Refresh
      </Btn>
    </div>
  );

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Management Dashboard"
        subtitle="A workshop-wise daily view of jobs, plates and client activity."
        actions={headerActions}
      />

      <div className="p-3 sm:p-6 space-y-6 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500">
          <span>
            Showing <strong className="text-zinc-800">{workshopFilter || "all workshops"}</strong> activity for <strong className="text-zinc-800">{formatDay(selectedDate, {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}</strong>
          </span>
          <span aria-live="polite">
            {lastUpdated ? "Auto-refreshes every minute | Updated " + lastUpdated.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
            }) : "Loading dashboard..."}
          </span>
        </div>

        {error && (
          <Card className="p-4 border-l-4 border-red-500 bg-red-50 text-sm text-red-700" testid="dashboard-error">
            {error}
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricCard
            label="Jobs Entered"
            value={data?.jobs_today}
            hint="All active jobs on this date"
            icon={Briefcase}
            accent="#08a9d6"
            testid="stat-jobs-today"
          />
          <MetricCard
            label="Total Plates"
            value={data?.plates_today}
            hint="Plates consumed on these jobs"
            icon={Stack}
            accent="#df1683"
            testid="stat-plates-today"
          />
          <MetricCard
            label="Baking Jobs"
            value={data?.baking_jobs}
            hint="Jobs marked for baking"
            icon={Fire}
            accent="#f2c500"
            testid="stat-baking-jobs"
          />
          <MetricCard
            label="Clients Served"
            value={data?.clients_served}
            hint="Distinct clients for the day"
            icon={UsersThree}
            accent="#18181b"
            testid="stat-clients-served"
          />
        </div>

        <section>
          <div className="mb-3">
            <h2 className="font-heading font-bold text-base text-zinc-950">Plate usage by system</h2>
            <p className="text-xs text-zinc-500">Selected-date output with current stock shown for reference.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 dashboard-machine-cards">
            {machines.map((machine, index) => {
              const output = (data?.machine_output_today || []).find(
                (row) => row.machine_id === machine.id
              );
              const stock = (data?.machine_stock || []).find(
                (row) => row.machine_id === machine.id
              );
              const accent = machineAccent(machine.name, index);
              return (
                <Card
                  key={machine.id}
                  testid={"machine-card-" + machine.id}
                  className="p-4 border-t-4 transition-transform duration-200 hover:-translate-y-0.5"
                  style={{ borderTopColor: accent }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-heading font-black text-lg text-zinc-950">{machine.name}</div>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accent }} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Plates used</div>
                      <div
                        className="font-mono text-2xl font-black text-zinc-950"
                        data-testid={"machine-output-" + machine.id}
                      >
                        {output?.plates ?? 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Jobs</div>
                      <div className="font-mono text-2xl font-black text-zinc-950">{output?.jobs ?? 0}</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-zinc-100 flex justify-between text-xs">
                    <span className="text-zinc-500">Current stock</span>
                    <strong className="font-mono text-zinc-800">{stock?.stock ?? 0}</strong>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card testid="top-clients-card">
            <SectionHeader
              title="Top five clients"
              subtitle="Ranked by total plate output on the selected date."
            />
            <div className="divide-y divide-zinc-100">
              {(data?.top_clients || []).map((client, index) => (
                <div
                  key={client.client_id}
                  className="px-4 py-3 flex items-center gap-3 transition-colors hover:bg-zinc-50"
                >
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black"
                    style={{
                      backgroundColor: ["#08a9d61a", "#df16831a", "#f2c5002a", "#18181b12", "#08a9d612"][index],
                      color: ["#0787ad", "#b41169", "#8a7000", "#18181b", "#0787ad"][index],
                    }}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-zinc-950 truncate">{client.client_name}</div>
                    <div className="text-xs text-zinc-500">{client.jobs} jobs</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-black text-xl text-zinc-950">{client.plates}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">plates</div>
                  </div>
                </div>
              ))}
              {(data?.top_clients || []).length === 0 && (
                <div className="px-4 py-12 text-center text-sm text-zinc-500">
                  No jobs were entered for this date.
                </div>
              )}
            </div>
          </Card>

          <Card testid="seven-day-trend-card">
            <SectionHeader
              title="Seven-day trend"
              subtitle="Daily jobs and plate volume ending on the selected date."
            />
            <div className="p-4 space-y-3">
              <div className="flex justify-end gap-4 text-[10px] uppercase tracking-wider text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <i className="w-2 h-2 rounded-full bg-cyan-500" /> Jobs
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="w-2 h-2 rounded-full bg-fuchsia-600" /> Plates
                </span>
              </div>
              {trend.map((row) => (
                <div key={row.date} className="grid grid-cols-[54px_1fr_70px] items-center gap-3">
                  <div className="text-xs font-semibold text-zinc-600">{formatDay(row.date)}</div>
                  <div className="space-y-1.5">
                    <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-cyan-500 transition-[width] duration-300"
                        style={{ width: (row.jobs / maxJobs) * 100 + "%" }}
                      />
                    </div>
                    <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-fuchsia-600 transition-[width] duration-300"
                        style={{ width: (row.plates / maxPlates) * 100 + "%" }}
                      />
                    </div>
                  </div>
                  <div className="font-mono text-[11px] text-right text-zinc-600">
                    <div>{row.jobs} jobs</div>
                    <div>{row.plates} plates</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
